import { useMemo } from "react";
import { formatValue, toneColor } from "../../lib/kernelFormat.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { KernelEmpty, useLinkLabels, linkLabel } from "./shared.jsx";

export const kind = "timeline";

// Vertical timeline. Each event sits on a visible left rail with a
// tone-colored square marker. We always surface bodyField when it
// exists so a timeline reads visibly different from a "rows" block —
// the body is the part that justifies the kind. Relative time ("5h
// ago") is the primary; the absolute timestamp lives on the title
// attribute so it's hover-revealable.
export function Component({ spec, data, onOpenScope }) {
    const rows = useMemo(() => {
        const arr = Array.isArray(data) ? data : [];
        return arr.slice(0, Math.max(1, spec.maxItems || 30));
    }, [data, spec.maxItems]);

    const linkSpec = spec.link;
    useLinkLabels(rows, linkSpec);

    if (!rows.length) return <KernelEmpty message={spec.empty || "no events"} />;

    const fmt = spec.format || "timestamp";

    return (
        <ol className="relative ml-[8px] border-l-[2px] border-[color:var(--line)] pl-[18px]">
            {rows.map((row, i) => {
                const tone = row?.[spec.toneField] || "neutral";
                const title = linkLabel(linkSpec, row, row?.[spec.titleField]);
                // Surface body even when bodyField isn't named — if the
                // row has a "body" or "description" column, use it.
                const body = pickBody(row, spec.bodyField);
                const time = row?.[spec.timeField];
                const absoluteText = time != null ? formatValue(time, fmt) : null;
                const relativeText = time != null ? relativeAgo(time) : null;
                const id = linkSpec?.idField ? row?.[linkSpec.idField] : null;
                const handler = id != null && onOpenScope ? () => onOpenScope({
                    dim: linkSpec.dim,
                    ids: [String(id)],
                    label: linkSpec.labelField ? row?.[linkSpec.labelField] : String(title ?? id),
                }) : null;
                const toneCol = toneColor(tone);
                return (
                    <li key={i} className="relative pb-[14px] last:pb-0">
                        {/* marker — punches across the rail and is
                            sized large enough to read as a deliberate
                            visual element, not a row dot. */}
                        <span
                            className="absolute top-[2px] inline-block h-[12px] w-[12px] rounded-[2px] border border-[color:var(--line)]"
                            style={{
                                left: "-25px",
                                background: sanitizeRenderColor(toneCol),
                                boxShadow: "0 0 0 3px var(--panel-soft)",
                            }}
                        />
                        <div className="flex items-baseline gap-[10px]">
                            <button
                                type="button"
                                onClick={handler || undefined}
                                disabled={!handler}
                                className="min-w-0 flex-1 text-left font-semibold text-[12px] leading-[1.25] text-[color:var(--ink)] hover:underline disabled:cursor-default disabled:no-underline truncate"
                                title={absoluteText ? `${title ?? ""} · ${absoluteText}` : String(title ?? "")}
                            >
                                {String(title ?? "")}
                            </button>
                            {relativeText ? (
                                <span
                                    className="shrink-0 whitespace-nowrap font-mono tabular-nums text-[10px] text-[color:var(--muted-soft)]"
                                    title={absoluteText || undefined}
                                >
                                    {relativeText}
                                </span>
                            ) : null}
                        </div>
                        {body ? (
                            <div className="mt-[3px] text-[11px] leading-[1.4] text-[color:var(--muted)] whitespace-pre-wrap break-words">
                                {String(body)}
                            </div>
                        ) : null}
                        {!body && absoluteText ? (
                            <div className="mt-[2px] text-[10px] text-[color:var(--muted-soft)] tabular-nums">
                                {absoluteText}
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ol>
    );
}

function pickBody(row, field) {
    if (field && row?.[field] != null) return row[field];
    if (!field) {
        if (row?.body != null) return row.body;
        if (row?.description != null) return row.description;
    }
    return null;
}

// Compact "5h ago" / "3d ago" formatter. Falls back to formatValue
// (timestamp) for things older than ~30 days so the date is still
// legible at long ranges.
function relativeAgo(v) {
    let t;
    if (typeof v === "number") t = v;
    else if (typeof v === "string") {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return null;
        t = d.getTime();
    } else return null;
    const diff = Date.now() - t;
    if (!Number.isFinite(diff)) return null;
    const future = diff < 0;
    const a = Math.abs(diff);
    const suffix = future ? "from now" : "ago";
    if (a < 60_000) return `just now`;
    if (a < 3_600_000) return `${Math.round(a / 60_000)}m ${suffix}`;
    if (a < 86_400_000) return `${Math.round(a / 3_600_000)}h ${suffix}`;
    if (a < 86_400_000 * 30) return `${Math.round(a / 86_400_000)}d ${suffix}`;
    return new Date(t).toLocaleDateString();
}
